/**
 * Step 6's body (f10.9): "Ready to release?" -- steps 1-5 with their committed
 * counts, every missing message, and the release button. Registered in
 * `STEP_BODIES["RELEASE_FOR_INPUT"]`.
 *
 * Nothing here decides readiness: `missing`, `complete`, `can_release` and
 * `blocked_reason` are the server's (`GET …/release-readiness`), and the
 * server recomputes them again when the release is posted.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, Send } from 'lucide-react';

import type { ReleaseReadiness } from '@/api/client';
import { useCycleReleaseReadiness } from '@/api/queries';

import type { StepBodyProps } from '../../CycleStepPage';
import { useCycleWorkspace } from '../../CycleWorkspaceLayout';
import { stepPillClass } from '../../workflowView';
import { ReleaseDialog } from './ReleaseDialog';
import { committedLabel, releasedLine } from './releaseView';

export function ReleaseChecklist({ cycleId, readiness }: { cycleId: string; readiness: ReleaseReadiness }) {
  return (
    <ul className="release-checklist">
      {readiness.steps.map((step) => {
        const ready = step.missing.length === 0;
        return (
          <li key={step.code} className="release-check">
            <span className={`release-check-icon ${ready ? 'is-ready' : 'is-missing'}`}>
              {ready ? (
                <Check aria-label="Ready" />
              ) : (
                <AlertTriangle aria-label="Missing data" />
              )}
            </span>
            <Link className="release-check-name" to={`/cycles/${cycleId}/steps/${step.code}`}>
              <span className="cycle-step-number mono">Step {step.number}</span>
              {step.name}
            </Link>
            <span className="release-check-count">
              {committedLabel(step.committed_batches, step.expected_batches)}
            </span>
            {step.status.code !== 'COMPLETED' ? (
              <span className={stepPillClass(step.status.code)}>{step.status.name}</span>
            ) : (
              <span />
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function ReleaseStep({ cycleId }: StepBodyProps) {
  const { cycle } = useCycleWorkspace();
  const readiness = useCycleReleaseReadiness(cycleId);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  if (readiness.error) {
    return (
      <div className="alert alert-danger" role="alert">
        <div className="a-body">
          {readiness.error instanceof Error ? readiness.error.message : 'Readiness could not be read.'}
        </div>
      </div>
    );
  }
  if (!readiness.data) return null;
  const data = readiness.data;
  const released = releasedLine(data);

  return (
    <div className="stack">
      <h4>Ready to release?</h4>
      <ReleaseChecklist cycleId={cycleId} readiness={data} />

      {data.missing.length > 0 && !released && (
        <div className="alert alert-warning" role="status">
          <AlertTriangle aria-hidden="true" />
          <div>
            <div className="a-title">Missing before a full release</div>
            <ul className="release-missing-list">
              {data.missing.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {notice && (
        <div className="alert alert-info" role="status">
          <div className="a-body">{notice}</div>
        </div>
      )}

      <div className="release-footer">
        {released ? (
          <span className="hint">{released}</span>
        ) : data.can_release ? (
          <>
            <span className="hint">Managers can start entering forecasts as soon as you release.</span>
            <button type="button" className="btn btn-primary" onClick={() => setConfirming(true)}>
              <Send aria-hidden="true" /> Release to managers
            </button>
          </>
        ) : (
          <span className="hint">{data.blocked_reason}</span>
        )}
      </div>

      {confirming && (
        <ReleaseDialog
          cycleId={cycleId}
          cycleVersion={cycle.version}
          readiness={data}
          onClose={() => setConfirming(false)}
          onReleased={setNotice}
        />
      )}
    </div>
  );
}
